import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import FileUpload from "@insite/mobius/FileUpload";
import Icon from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import Typography from "@insite/mobius/Typography";
import { ATTACHMENT_LIMITS } from "@thkStore/Constants/ContactUs/attachmentLimits";
import { ContactFormContext } from "@thkStore/Overrides/Widgets/ContactUs/ContactForm";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    label = "label",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
    };
}

const isSameFile = (a: File, b: File) => a.name === b.name && a.size === b.size;

const Attachment: React.FC<OwnProps> = ({ fields }) => {
    const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
    const [errorMessage, setErrorMessage] = React.useState("");
    const contactFormContext = React.useContext(ContactFormContext);

    contactFormContext.getAttachments = () => selectedFiles;
    contactFormContext.validators.attachment = () => validate(selectedFiles);
    contactFormContext.clearAttachments = () => {
        setSelectedFiles([]);
        setErrorMessage("");
    };

    const validate = (files: File[]): boolean => {
        if (files.length === 0) {
            setErrorMessage("");
            return true; // attachments are optional
        }
        if (files.length > ATTACHMENT_LIMITS.maxFileCount) {
            setErrorMessage(siteMessage("ContactUsForm_AttachmentTooManyErrorMessage") as string);
            return false;
        }
        const totalSize = files.reduce((sum, file) => sum + file.size, 0);
        if (totalSize > ATTACHMENT_LIMITS.maxTotalSizeBytes) {
            setErrorMessage(siteMessage("ContactUsForm_AttachmentTotalSizeErrorMessage") as string);
            return false;
        }
        for (const file of files) {
            if (file.size > ATTACHMENT_LIMITS.maxSizeBytes) {
                setErrorMessage(siteMessage("ContactUsForm_AttachmentTooLargeErrorMessage") as string);
                return false;
            }
            if (!ATTACHMENT_LIMITS.allowedMimeTypes.includes(file.type as any)) {
                setErrorMessage(siteMessage("ContactUsForm_AttachmentInvalidFormatErrorMessage") as string);
                return false;
            }
            const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
            if (!ext || !ATTACHMENT_LIMITS.allowedExtensions.includes(ext as any)) {
                setErrorMessage(siteMessage("ContactUsForm_AttachmentInvalidExtensionErrorMessage") as string);
                return false;
            }
        }
        setErrorMessage("");
        return true;
    };

    const fileChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const incoming = Array.from(event.target.files ?? []);
        // The native input replaces its selection on every open; clear it so the
        // accumulated array stays the source of truth and removed files can be re-picked.
        event.target.value = "";
        if (incoming.length === 0) {
            return;
        }
        const merged = [...selectedFiles];
        for (const file of incoming) {
            if (!merged.some(existing => isSameFile(existing, file))) {
                merged.push(file);
            }
        }
        setSelectedFiles(merged);
        validate(merged);
    };

    const removeFile = (target: File) => {
        const next = selectedFiles.filter(file => !isSameFile(file, target));
        setSelectedFiles(next);
        validate(next);
    };

    return (
        <div>
            <FileUpload
                label={fields.label}
                accept={ATTACHMENT_LIMITS.allowedMimeTypes.join(",")}
                onFileChange={fileChangeHandler as any}
                buttonText="Select files"
                error={errorMessage}
                multiple
                cssOverrides={{ appearanceTransition: css`display: none;` }}
            />
            {selectedFiles.length > 0 && (
                <div style={{ marginTop: "8px" }}>
                    {selectedFiles.map(file => (
                        <div
                            key={`${file.name}-${file.size}`}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "4px 0",
                            }}
                        >
                            <Typography size={13}>{file.name}</Typography>
                            <Clickable
                                onClick={() => removeFile(file)}
                                data-test-selector={`contactUsRemoveAttachment_${file.name}`}
                                css={css`
                                    flex-shrink: 0;
                                    margin-left: 8px;
                                    padding: 4px;
                                    &:hover { opacity: 0.7; }
                                `}
                            >
                                <Icon src={XCircle} size={16} />
                            </Clickable>
                        </div>
                    ))}
                </div>
            )}
            <Typography
                size={12}
                color="text.accent"
                css={css`
                    display: block;
                    margin-top: 4px;
                `}
            >
                {siteMessage("ContactUsForm_AttachmentHelperText")}
            </Typography>
        </div>
    );
};

const widgetModule: WidgetModule = {
    component: Attachment,
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Attachment",
            },
        ],
    },
};

export default widgetModule;
